import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, SectionTag } from "./Reveal";
import { PHASES } from "../data/content";

const StatusChip = ({ status }) => (
  <span
    className={`font-mono text-[9px] tracking-[0.2em] px-2 py-1 border ${
      status === "IN DEVELOPMENT" ? "border-gold/60 text-gold" : "border-tech/50 text-tech"
    }`}
  >
    {status}
  </span>
);

export default function Roadmap() {
  const [active, setActive] = useState(0);

  return (
    <section id="roadmap" data-testid="roadmap-section" className="relative py-24 lg:py-32 bg-abyss/40">
      <div className="relative max-w-[1600px] mx-auto px-4 sm:px-8 lg:px-12">
        <Reveal>
          <SectionTag index="05" label="Proposed Expedition Roadmap" />
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2 className="font-display font-bold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl text-ice leading-[0.95] max-w-3xl">
              Five phases to <span className="text-polar">the ice</span>
            </h2>
            <p className="max-w-md text-sm text-ice/60 leading-relaxed lg:text-right font-mono tracking-wide">
              DEC 2027 – 2029 IS A TARGET PLANNING WINDOW — NOT A CONFIRMED DEPARTURE DATE.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-16" data-testid="roadmap-timeline">
            <div className="relative">
              <div className="absolute left-0 right-0 top-[22px] h-px bg-polar/15 hidden sm:block" aria-hidden="true" />
              <div className="grid grid-cols-1 sm:grid-cols-5 gap-6 sm:gap-3">
                {PHASES.map((p, i) => (
                  <button
                    key={p.num}
                    onClick={() => setActive(i)}
                    aria-pressed={active === i}
                    data-testid={`roadmap-phase-${p.num}`}
                    className="relative text-left group focus:outline-none focus-visible:ring-2 focus-visible:ring-polar"
                  >
                    <span
                      className={`relative z-10 flex w-11 h-11 items-center justify-center rounded-full border font-mono text-xs transition-colors duration-300 ${
                        active === i
                          ? "bg-mission border-mission text-ice"
                          : "bg-abyss border-polar/30 text-polar group-hover:border-polar"
                      }`}
                    >
                      {p.num}
                    </span>
                    <span className={`block mt-4 font-display font-semibold text-lg tracking-wide leading-tight transition-colors duration-300 ${active === i ? "text-ice" : "text-tech group-hover:text-ice"}`}>
                      {p.title}
                    </span>
                    <span className="block mt-2">
                      <StatusChip status={p.status} />
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-12 border border-polar/15 bg-[#0B1F35]/80 backdrop-blur-md p-7 sm:p-10 min-h-[220px]" data-testid="roadmap-detail-panel">
              <AnimatePresence mode="wait">
                <motion.div
                  key={active}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                >
                  <div className="flex flex-wrap items-center gap-4">
                    <span className="font-mono text-xs tracking-[0.3em] text-mission">PHASE {PHASES[active].num}</span>
                    <StatusChip status={PHASES[active].status} />
                  </div>
                  <h3 className="font-display font-bold text-2xl sm:text-4xl text-ice mt-3 tracking-wide uppercase">
                    {PHASES[active].title}
                  </h3>
                  <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-3">
                    {PHASES[active].items.map((it) => (
                      <li key={it} className="flex gap-3 text-sm sm:text-base text-ice/70 leading-snug">
                        <span className="text-polar shrink-0">—</span>
                        {it}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
